import { FormattedMessage } from "react-intl";
import Card from "reactstrap/lib/Card";
import CardBody from "reactstrap/lib/CardBody";

export const AnalysisTabs = {
  OVERVIEW: "overview",
  BACKEND: "backend",
  CLIENT: "client",
  SUPPORT: "support",
};

// One place decides which kinds of finding belong to which tab. The tab strip, the
// overview table and each tab's own list all read from here.
export const FindingTabs = [
  {
    id: AnalysisTabs.BACKEND,
    labelId: "DailyAnalysisBackend",
    kinds: ["iis", "applog"],
  },
  {
    id: AnalysisTabs.CLIENT,
    labelId: "DailyAnalysisClientErrors",
    kinds: ["clienterror"],
  },
  {
    id: AnalysisTabs.SUPPORT,
    labelId: "DailyAnalysisSupport",
    kinds: ["support"],
  },
];

// data is undefined while the day is still loading, and the tab strip renders before it.
export function findingsForTab(data, tabId) {
  const tab = FindingTabs.find((t) => t.id === tabId);
  return (data && data.findings ? data.findings : []).filter((f) =>
    tab.kinds.includes(f.kind),
  );
}

export const formatNumber = (value) =>
  typeof value === "number" ? value.toLocaleString("sv-SE") : "—";

// Compact form for the large numbers in the cards: 6 119 241 becomes "6,12 M".
export const formatCompact = (value) => {
  if (typeof value !== "number") return "—";
  if (value >= 1000000) {
    return `${(value / 1000000).toLocaleString("sv-SE", {
      maximumFractionDigits: 2,
    })} M`;
  }
  return value.toLocaleString("sv-SE");
};

export const formatSeconds = (ms) => {
  if (typeof ms !== "number") return "—";
  const seconds = ms / 1000;
  const decimals = seconds < 10 ? 1 : 0;
  return seconds.toLocaleString("sv-SE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export function StatCard({ labelId, value, note, tone }) {
  return (
    <Card className="da-stat">
      <CardBody>
        <div className="da-stat__label">
          <FormattedMessage id={labelId} />
        </div>
        <div
          className={
            tone ? `da-stat__value da-stat__value--${tone}` : "da-stat__value"
          }
        >
          {value}
        </div>
        <div className="da-stat__note">{note}</div>
      </CardBody>
    </Card>
  );
}

// What happened at the same time, in an extra column. Only support tickets carry it, and
// only when there is something to show - an empty column would still cost a grid gap on
// every other tab. It is co-occurrence, never a link: the app log and IIS have no user to
// join on, which is what the title says.
const hasNearby = (nearby) =>
  !!nearby &&
  ((nearby.appLogErrors || 0) > 0 || (nearby.serverErrors || 0) > 0);

function NearbyColumn({ nearby }) {
  const appLog = nearby.appLogErrors || 0;
  const server = nearby.serverErrors || 0;
  return (
    <span className="da-find__nearby">
      {appLog > 0 ? (
        <span>
          <FormattedMessage
            id="DailyAnalysisNearbyAppLog"
            values={{ count: formatNumber(appLog) }}
          />
        </span>
      ) : (
        ""
      )}
      {server > 0 ? (
        <span>
          <FormattedMessage
            id="DailyAnalysisNearbyServer"
            values={{ count: formatNumber(server) }}
          />
        </span>
      ) : (
        ""
      )}
    </span>
  );
}

export function Finding({ finding, children }) {
  const severity = finding.severity || "warn";
  const nearby = hasNearby(finding.nearby);
  return (
    <details
      className={`da-find da-find--${severity}${nearby ? " da-find--nearby" : ""}`}
    >
      <summary>
        <span className={`da-find__tag da-find__tag--${severity}`}>
          {finding.tag}
        </span>
        <span className="da-find__main">
          <h4>{finding.title}</h4>
          {finding.why ? <div className="da-find__why">{finding.why}</div> : ""}
        </span>
        {nearby ? <NearbyColumn nearby={finding.nearby} /> : ""}
        {finding.figure ? (
          <span className="da-find__figure">
            {finding.figure.value}
            <small>{finding.figure.unit}</small>
          </span>
        ) : (
          ""
        )}
      </summary>
      <div className="da-find__body">
        {/* The heading is the only thing separating a guess from the measured figure
            above it. prompt.md points at it by name and asks the model to match its
            voice, so the two have to stay in step. */}
        <div className="da-find__text">
          {children}
          {finding.explanation ? (
            <>
              <h5>
                <FormattedMessage id="DailyAnalysisHypothesis" />
              </h5>
              <p>{finding.explanation}</p>
            </>
          ) : (
            ""
          )}
        </div>
        {finding.nextStep ? (
          <div className="da-find__next">
            <h5>
              <FormattedMessage id="DailyAnalysisNextStep" />
            </h5>
            <p>{finding.nextStep}</p>
          </div>
        ) : (
          ""
        )}
      </div>
    </details>
  );
}

// An empty list is normal in a healthy week and should read as a result, not as a
// broken page.
export function FindingList({ findings }) {
  if (!findings || findings.length === 0) {
    return (
      <p className="da-empty">
        <FormattedMessage id="DailyAnalysisNothingToAct" />
      </p>
    );
  }
  return (
    <div className="d-flex flex-column gap-2">
      {findings.map((finding) => (
        <Finding key={finding.key} finding={finding} />
      ))}
    </div>
  );
}

export function BarRow({ label, value, max, tone, display }) {
  const width = max > 0 ? Math.max((value / max) * 100, 1) : 0;
  return (
    <div className="da-row">
      <div>
        <div className="da-row__label">{label}</div>
        <div className="da-bar">
          <i
            className={
              tone ? `da-bar__fill da-bar__fill--${tone}` : "da-bar__fill"
            }
            style={{ width: `${width}%` }}
          />
        </div>
      </div>
      <span className="da-row__value">{display || formatNumber(value)}</span>
    </div>
  );
}

export function DayBars({ points, labelEvery = 1 }) {
  if (!points || points.length === 0) return "";
  const max = Math.max(...points.map((p) => p.count));
  const peak = points.findIndex((p) => p.count === max);
  return (
    <div className="da-daybars">
      {points.map((point, i) => (
        <div
          className="da-daybars__col"
          key={i}
          title={`${point.label}: ${formatNumber(point.count)}`}
        >
          <div className="da-daybars__track">
            <div
              className={
                i === peak
                  ? "da-daybars__bar da-daybars__bar--peak"
                  : "da-daybars__bar"
              }
              style={{
                height: `${max > 0 ? Math.max((point.count / max) * 100, 2) : 0}%`,
              }}
            />
          </div>
          <div className="da-daybars__label">
            {i % labelEvery === 0 ? point.label : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PanelCard({ titleId, titleValues, subtitle, children }) {
  return (
    <Card className="da-panel">
      <CardBody>
        <h6>
          <FormattedMessage id={titleId} values={titleValues} />
        </h6>
        {/* Always rendered. Without it a panel with a subtitle starts one row lower
            than its neighbour and the row looks broken. min-height is in the CSS. */}
        <p className="da-panel__sub">{subtitle}</p>
        {children}
      </CardBody>
    </Card>
  );
}
